<template>
    <div class="campaign-builder-wrapper">
        <KonvaCampaignContextMenu
            data-qa="konva-campaign-context-menu"
            :context-menu-data="contextMenuData"
            :show-reporting-view="showReportingView"
            :preset="preset"
            :flags-info="flagsInfo"
            @close="onCloseContextMenu"
            @click-active-stats="() => openReportingView($options.STATUS_FILTER.ACTIVE)"
            @click-queue-stats="() => openReportingView($options.STATUS_FILTER.QUEUED)"
            @click-waiting-stats="() => openReportingView($options.STATUS_FILTER.WAITING)"
            @click-view-and-edit="openConfigModal"
            @click-sequence-toggle-ready="toggleSequenceReady"
            @click-new-email-experience="clickNewEmailExperience"
            @click-delete="deleteCell"
            @click-delete-all="() => deleteCells()"
            @click-duplicate="duplicateSingleCell"
            @click-duplicate-all="duplicateMultipleCells"
            @click-rename="openRenameModal"
            @click-edit-note="onEditNote"
            @click-settings="openSettingsModal"
        />

        <div v-if="showNeverPublishedWarning" class="reporting-warning">
            <DsInlineAlert type="warning">
                {{ $t('funnel.campaign.reporting.not.published.warning') }}
            </DsInlineAlert>
        </div>

        <div
            v-if="!showNeverPublishedWarning"
            class="campaign-builder-content"
            :class="{ 'reporting-view': showReportingView }"
        >
            <KonvaAutomationBetweenAddMenu
                v-if="bigCardInlineAddEnabled && inlineBetweenAddData"
                :enabled-cell-types="enabledCellTypes"
                :inline-between-add-data="inlineBetweenAddData"
                :selected-sequence="selectedSequence"
                :cells="cells"
                @add-cell-between="onAddCellBetween"
            />
            <KonvaAutomationInlineAddMenu
                v-if="bigCardInlineAddEnabled && inlineAddData"
                :selected-sequence="selectedSequence"
                :inline-add-data="inlineAddData"
                :enabled-cell-types="enabledCellTypes"
                :enabled-integrations="enabledIntegrations"
                @add-cell="addCell"
            />

            <KonvaCampaignStage
                ref="stage"
                data-qa="main-canvas"
                show-hotkeys
                selected-sequence="1"
                :debug-mode="debugMode"
                :report-data="reportData"
                :show-sidebar="isSidebarOpen"
                :is-inline-add-open="!!inlineAddData"
                :show-reporting-view="showReportingView"
                :email-stats="emailStats"
                :unique-contacts="uniqueContacts"
                :cells="cells"
                :published-cells="publishedCells"
                :preset="preset"
                :enabled-cell-types="enabledCellTypes"
                :enabled-integrations="enabledIntegrations"
                :selected-cells="selectedCells"
                :flags-info="flagsInfo"
                :editable-name-data="editableNameData"
                :show-config-modal="showConfigModal"
                :beta-features-enabled="betaFeaturesEnabled"
                :initial-zoom-level="currentMainZoomLevel"
                @on-open-filter-modal="showReportingFilterModal = true"
                @on-update-filters="onUpdateFilters"
                @open-element-list="$emit('open-element-list')"
                @open-element-list-sidebar="$emit('open-element-list-sidebar')"
                @open-inline-add="openInlineAdd"
                @close-inline-add="closeInlineAddMenu"
                @open-between-add="openAddBetweenMenu"
                @enable-debug-mode="enableDebugMode"
                @set-selected-cells="setSelectedCells"
                @resize="onResize"
                @click-cell="onClickCell"
                @click-stage="onClickStage"
                @show-hotkeys="showHotkeys"
                @toggle-hotkeys="showHotkeys"
                @set-sequence-id="setSequenceId"
                @copy-cells="copyCells"
                @move-cells="moveCells"
                @update-note="updateNote"
                @rename-cell="renameCell"
                @delete-cells="deleteCells"
                @add-cell="addCell"
                @open-config-modal="openConfigModal"
                @open-automation-feedback-modal="openFeedbackModal"
                @set-editable-name-data="setEditableNameData"
                @update-note-color="updateNotesColor"
                @open-email-performance-report="openEmailPerformanceReport"
                @open-report="openReport"
                @open-sequence-email-report="openSequenceEmailReport"
                @update-initial-zoom-level="updateMainZoomLevel"
            />

            <KonvaCampaignSequenceModal
                v-if="currentSequenceCell"
                data-qa="sequence-canvas-modal"
                :debug-mode="debugMode"
                :sequence-zoom-level="currentMainZoomLevel"
                :published-cells="publishedCells"
                :current-sequence-cell="currentSequenceCell"
                :is-spa-app="isSpaApp"
                :report-data="reportData"
                :is-sidebar-open="isSidebarOpen && !sidebarLocked"
                :is-inline-add-open="!!inlineAddData"
                :show-reporting-view="showReportingView"
                :cells="cells"
                :preset="preset"
                :enabled-cell-types="enabledCellTypes"
                :enabled-integrations="enabledIntegrations"
                :funnel-info="funnelInfo"
                :selected-cells="selectedCells"
                :date-range="dateRange"
                :flags-info="flagsInfo"
                :editable-name-data="editableNameData"
                :show-config-modal="showConfigModal"
                :beta-features-enabled="betaFeaturesEnabled"
                @on-open-filter-modal="showReportingFilterModal = true"
                @on-update-filters="onUpdateFilters"
                @open-element-list="() => $emit('open-element-list')"
                @open-inline-add="openInlineAdd"
                @close-inline-add="closeInlineAddMenu"
                @open-between-add="openAddBetweenMenu"
                @set-selected-cells="setSelectedCells"
                @resize="onResize"
                @click-cell="onClickCell"
                @click-stage="onClickStage"
                @copy-cells="copyCells"
                @move-cells="moveCells"
                @update-note="updateNote"
                @rename-cell="renameCell"
                @delete-cells="deleteCells"
                @add-cell="addCell"
                @open-config-modal="openConfigModal"
                @toggle-sequence-ready="toggleSequenceReady"
                @on-close-sequence="onCloseSequence"
                @open-copy-from-automation="openCopyFromAutomationModal"
                @open-edit-sequence-name="openEditSequenceName"
                @open-automation-feedback-modal="openFeedbackModal"
                @set-editable-name-data="setEditableNameData"
                @update-note-color="updateNotesColor"
                @update-cell-value="updateCellValue"
            />

            <SequenceAndEmailReport
                :is-open="openEmailSequenceReport"
                :funnel-id="funnelId"
                :date-range="dateRange"
                :is-uk-date-locale="isUkDateLocale"
                @close-sequence-email-report-modal="closeSequenceEmailReport"
            />

            <ReportingFiltersModal
                v-if="showReportingFilterModal"
                :date-range="dateRange"
                :preset="preset"
                @on-update-filters="onUpdateFilters"
                @close="showReportingFilterModal = false"
            />

            <KonvaDeleteElement
                v-if="showDeleteDialog"
                :is-open="showDeleteDialog"
                :elements-to-delete="elementsToDelete"
                :cells="draftJson.cells"
                :role-info="roleInfo"
                @close-delete-dialog="closeDeleteDialog"
                @confirm-delete="confirmDeleteDialog"
            />

            <CopyFromAutomation
                v-if="showCopyFromAutomationModal"
                @replace-sequence="({ otherJson, otherSequenceId }) => replaceSequence({ otherJson, otherSequenceId, selectedSequence })"
                @close-modal="closeCopyFromAutomationModal"
            />

            <EditSequenceName
                v-if="showEditSequenceName"
                :sequence-cell="currentSequenceCell"
                :is-spa-app="isSpaApp"
                @update-name="updateSequenceName"
                @close-modal="closeEditSequenceName"
            />
        </div>
    </div>
</template>

<script>
import {
    processDecisionCells,
    containsIncompleteDecisionDiamond,
} from '../decisionDiamonds/decision-utils';
import {
    formatDateAsString,
} from '../utils/date-utils';
import {
    findLockedIncomingCells,
    findOutgoingCells,
    findOutgoingEdges,
    findIncomingEdges,
    isEdgeFromConfirmToLinkClick,
} from '../utils/cell/automation-edge-utils';
import { getCellById, findChildren } from '../utils/cell/automation-cell-utils';
import KonvaAutomationInlineAddMenu from './KonvaAutomationInlineAddMenu.vue';
import KonvaCampaignStage from './KonvaCampaignStage.vue';
import SequenceAndEmailReport from '../reporting/SequenceAndEmailReport.vue';
import KonvaCampaignSequenceModal from './KonvaCampaignSequenceModal.vue';
import CopyFromAutomation from '../configuration/sequence/CopyFromAutomation.vue';
import EditSequenceName from '../configuration/sequence/EditSequenceName.vue';
import ReportingFiltersModal from '../reporting/ReportingFiltersModal.vue';
import KonvaCampaignContextMenu from './KonvaCampaignContextMenu.vue';
import KonvaDeleteElement from './KonvaDeleteElement.vue';
import {
    getDateRangeStats,
    getActiveStats,
    getFunnelStats,
    getEmailStats,
    getDateRangeEmailStats,
    getDateRangeUniqueContactsStats,
    getUniqueContactsStats,
} from '../campaign.api';
import {
    PRESET_FILTERS,
    DECISION_TYPE,
    FLOW_TYPE,
    EDGE_TYPE,
    GOALS,
    MAIN_CANVAS_ID,
    STYLE_BY_META_TYPE,
    TIMERS,
    NOTE_COLOR,
    NOTES_TYPE,
} from '../utils/editor.constants';
import { copyCell } from '../utils/konva-copy-utils';
import {
    maxCellIdStore,
} from '../utils/max-cell-id-utils';
import KonvaAutomationBetweenAddMenu
from './KonvaAutomationBetweenAddMenu.vue';

const STATUS_FILTER = {
    ACTIVE: 'Active',
    QUEUED: 'Queued',
    WAITING: 'Waiting',
    DONE: 'Done',
};

const ZOOM_LEVEL_50_PERCENT = 1;
const MINIMUM_ZOOM_TO_SHOW_BIG_CARDS = 1.5;

export default {
    STATUS_FILTER,
    MAIN_CANVAS_ID,
    components: {
        KonvaAutomationBetweenAddMenu,
        SequenceAndEmailReport,
        KonvaAutomationInlineAddMenu,
        KonvaCampaignStage,
        ReportingFiltersModal,
        KonvaCampaignSequenceModal,
        CopyFromAutomation,
        EditSequenceName,
        KonvaCampaignContextMenu,
        KonvaDeleteElement,
    },
    props: {
        enabledIntegrations: {
            type: Array,
            default: () => [],
        },
        selectedSequence: {
            type: String,
            default: MAIN_CANVAS_ID,
        },
        draftJson: {
            type: Object,
            default: () => ({}),
        },
        publishedJson: {
            type: Object,
            default: () => ({}),
        },
        showReportingView: {
            type: Boolean,
            default: false,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        funnelInfo: {
            type: Object,
            default: () => ({}),
        },
        flagsInfo: {
            type: Object,
            default: () => ({}),
        },
        roleInfo: {
            type: Object,
            default: () => ({}),
        },
        isSpaApp: {
            type: Boolean,
            default: false,
        },
        enabledCellTypes: {
            type: Array,
            default: () => ([]),
        },
        selectedCells: {
            type: Array,
            default: () => [],
        },
        showConfigModal: {
            type: Boolean,
            default: false,
        },
        betaFeaturesEnabled: {
            type: Boolean,
            required: true,
        },
    },
    emits: [
        'update-cells',
        'update-cell-name',
        'update-cells-global-state',
        'add-cell',
        'add-cell-between',
        'toggle-sequence-ready',
        'open-rename-modal',
        'open-settings-modal',
        'open-config-modal',
        'open-element-list',
        'open-element-list-sidebar',
        'set-selected-cells',
        'set-sequence-id',
        'save-campaign',
        'replace-sequence',
        'open-new-email-experience',
        'open-automation-feedback-modal',
        'toggle-keyboard-shortcuts-sidebar',
        'update-cell-value',
    ],
    data() {
        const startDate = new Date();
        const endDate = new Date();

        startDate.setDate(endDate.getDate() - 10);
        const funnelId = new URL(window.location.href).searchParams.get('funnelId');

        return {
            openEmailSequenceReport: false,
            editableNameData: null,
            showCopyFromAutomationModal: false,
            showEditSequenceName: false,
            showDeleteDialog: false,
            showConvertModal: false,
            bardEmailCell: undefined,
            elementsToDelete: [],
            ddCellsJoinGoals: [],
            showReportingFilterModal: false,
            emailStats: {
                sentCount: 0,
                openedCount: 0,
                clickedCount: 0,
                optOutCount: 0,
                bounceCount: 0,
                complaintCount: 0,
                percentBounced: 0,
                percentClicked: 0,
                percentOpened: 0,
                percentOptedOut: 0,
                percentUnopened: 0,
                deliveredCount: 0,
                unopenedCount: 0,
                percentComplained: 0,
                clickToOpenRatio: 0,
            },
            uniqueContacts: 0,
            reportData: {},
            preset: PRESET_FILTERS.LAST_7_DAYS,
            dateRange: [startDate, endDate],
            funnelId,
            debugMode: false,
            contextMenuData: null,
            inlineAddData: null,
            inlineBetweenAddData: null,
            currentMainZoomLevel: ZOOM_LEVEL_50_PERCENT,
        };
    },

    computed: {
        effectiveDateRange() {
            if (this.preset === PRESET_FILTERS.CUSTOM) return this.dateRange;

            if (this.preset === PRESET_FILTERS.LAST_24_HOURS) {
                const date1DayAgo = new Date(new Date().setDate(new Date().getDate() - 1));

                return [date1DayAgo, new Date()];
            }

            if (this.preset === PRESET_FILTERS.LAST_7_DAYS) {
                const date7DaysAgo = new Date(new Date().setDate(new Date().getDate() - 7));

                return [date7DaysAgo, new Date()];
            }

            if (this.preset === PRESET_FILTERS.LAST_30_DAYS) {
                const date30DaysAgo = new Date(new Date().setDate(new Date().getDate() - 30));

                return [date30DaysAgo, new Date()];
            }

            return null;
        },

        isSequence() {
            return this.selectedSequence !== MAIN_CANVAS_ID;
        },

        currentSequenceCell() {
            if (!this.isSequence || !this.selectedSequence) return null;

            return this.cells.find((cell) => cell.id === this.selectedSequence);
        },

        campaignJson() {
            return this.showReportingView
                ? this.publishedJson
                : this.draftJson;
        },

        publishedCells() {
            return this.publishedJson?.cells || [];
        },

        cells() {
            return this.campaignJson?.cells || [];
        },

        showNotPublishedWarning() {
            return (!!this.showReportingView && !this.funnelInfo.published);
        },

        showNeverPublishedWarning() {
            return !!this.showReportingView && !this.publishedCells.length;
        },

        isSidebarOpen() {
            return !this.showReportingView;
        },

        sidebarLocked() {
            let sidebarIsLocked = false;

            if (this.isSequence) {
                const sequence = getCellById(this.selectedSequence, this.cells);

                sidebarIsLocked = sequence?.value?.flowItemsLocked === '1';
            }


            return sidebarIsLocked;
        },

        bigCardInlineAddEnabled() {
            return this.flagsInfo.bigCardInlineAdd && this.betaFeaturesEnabled;
        },

        isUkDateLocale() {
            return Boolean(this.appInfo.ukDateLocale);
        },

        isTranslatedUkDateLocale() {
            return this.appInfo.translatedUkDateLocale;
        },
    },

    watch: {
        showReportingView: {
            handler() {
                if (this.showReportingView) this.fetchReportData();
                if (this.showNotPublishedWarning) this.$toast({ message: this.$t('funnel.edit.notPublished') });
            },
        },
    },

    mounted() {
        if (this.showReportingView) this.fetchReportData();
        if (this.showNotPublishedWarning) this.$toast({ message: this.$t('funnel.edit.notPublished') });
    },

    methods: {
        updateMainZoomLevel(zoomLevel) {
            this.currentMainZoomLevel = zoomLevel;
        },

        openSequenceEmailReport() {
            this.openEmailSequenceReport = true;
        },

        openReport(type) {
            const params = new URLSearchParams();

            params.append('view', 'resultsPage');
            params.append('reportClass', type);
            params.append('hideSpaNav', 'true');
            params.append('FunnelId_DATA', this.funnelId);

            window.open(`${this.appInfo.baseUrl}/Reports/searchTemplate.jsp?${params.toString()}`, '_blank');
        },

        closeSequenceEmailReport() {
            this.openEmailSequenceReport = false;
        },

        openEmailPerformanceReport(type) {
            const startDate = formatDateAsString(this.effectiveDateRange[0], !this.isTranslatedUkDateLocale);
            const endDate = formatDateAsString(this.effectiveDateRange[1], !this.isTranslatedUkDateLocale);
            const params = new URLSearchParams();

            params.append('FunnelId_DATA', this.funnelId);
            params.append('view', 'default');
            params.append('reportClass', type);
            params.append('SentDate_DATA', startDate);
            params.append('SentDate_DATA2', endDate);
            params.append('ActionDate_DATA', startDate);
            params.append('ActionDate_DATA2', endDate);
            params.append('fromMenu', 'true');
            params.append('hideSpaNav', 'true');
            params.append('view', 'resultsPage');
            params.append('RunSearch', 'true');

            window.open(`${this.appInfo.baseUrl}/Reports/searchTemplate.jsp?${params.toString()}`, '_blank');
        },

        setEditableNameData(editableNameData) {
            this.editableNameData = editableNameData;
        },

        openInlineAdd(inlineAddData) {
            this.setSelectedCells([]);
            this.inlineAddData = inlineAddData;
        },

        onAddCellBetween(event) {
            const data = {
                sourceElement: this.inlineBetweenAddData.sourceElement,
                cellDefaultType: event.name,
                cellName: event.displayLabel,
                isSequenceView: this.inlineBetweenAddData.isSequenceView,
                isBigCard: this.currentMainZoomLevel >= MINIMUM_ZOOM_TO_SHOW_BIG_CARDS,
            };

            this.$emit('add-cell-between', data);
            this.closeInlineBetweenAddMenu();
        },

        openAddBetweenMenu(addBetweenData) {
            this.setSelectedCells([]);
            this.inlineBetweenAddData = addBetweenData;
        },

        closeInlineAddMenu() {
            this.inlineAddData = null;
        },

        closeInlineBetweenAddMenu() {
            this.inlineBetweenAddData = null;
        },

        enableDebugMode() {
            this.debugMode = true;
        },
        onResize() {
            if (this.inlineAddData) this.closeInlineAddMenu();
            if (this.inlineBetweenAddData) this.closeInlineBetweenAddMenu();
            if (this.contextMenuData) this.onCloseContextMenu();
        },

        onClickCell({ event, cell }) {
            if (this.inlineAddData) this.closeInlineAddMenu();
            if (this.inlineBetweenAddData) this.closeInlineBetweenAddMenu();

            this.setEditableNameData(null);
            let contextMenuData = null;
            const isSelected = this.selectedCells.map((selectedCell) => selectedCell.meta.id).includes(cell.meta.id);

            if (event.evt.shiftKey && !this.showReportingView) {
                if (isSelected) { // unselect cell
                    this.$emit('set-selected-cells', this.selectedCells.filter((selectedCell) => selectedCell.meta.id !== cell.meta.id));
                } else { //  add to selected cells
                    this.$emit('set-selected-cells', [...this.selectedCells, cell]);
                }
            } else if (isSelected) {
                contextMenuData = {
                    cells: this.cells, cell, event, selectedCells: this.selectedCells,
                };
            } else {
                if (!this.showReportingView) this.$emit('set-selected-cells', [cell]);
                contextMenuData = {
                    cells: this.cells, cell, event, selectedCells: [cell],
                };
            }

            this.contextMenuData = contextMenuData;
        },

        openReportingView(statusFilter = STATUS_FILTER.ACTIVE) {
            if (!this.contextMenuData) return;

            const {
                appInfo,
                funnelInfo,
                contextMenuData,
                effectiveDateRange,
            } = this;
            const { cell } = contextMenuData;
            const params = new URLSearchParams();

            if (cell.meta.parent !== MAIN_CANVAS_ID) {
                const flowData = `${funnelInfo.funnelId}:${contextMenuData.cell.meta.parent}`;

                if (statusFilter !== STATUS_FILTER.WAITING) {
                    const flowItemData = `${flowData}:${contextMenuData.cell.id}`;

                    params.append('reportClass', 'FunnelQueuedFlowItem');
                    params.append('Flow_DATA', flowData);
                    params.append('FlowItem_DATA', flowItemData);

                    if (effectiveDateRange) {
                        params.append('ProcessedDate_DATA', formatDateAsString(effectiveDateRange[0], !this.isTranslatedUkDateLocale));
                        params.append('ProcessedDate_DATA2', formatDateAsString(effectiveDateRange[1], !this.isTranslatedUkDateLocale));
                    }
                } else {
                    const outgoingCells = findOutgoingCells(contextMenuData.cell.id, this.cells);
                    const targetFlowItem = outgoingCells?.length && outgoingCells[0].id;

                    params.append('reportClass', 'FunnelFlowRecipientWaiting');
                    params.append('Flow_DATA', flowData);

                    if (targetFlowItem) {
                        params.append('FlowItem_DATA', `${flowData}:${targetFlowItem}`);
                    } else {
                        const altTargetFlowItem = `-${contextMenuData.cell.id}`;

                        params.append('FlowItem_DATA', `${flowData}:${altTargetFlowItem}`);
                    }
                }
            } else if (cell.type === FLOW_TYPE) {
                params.append('reportClass', 'FunnelFlowRecipient');
                params.append('Flow_DATA', `${funnelInfo.funnelId}:${contextMenuData.cell.id}`);

                if (effectiveDateRange) {
                    params.append('StopDate_DATA', formatDateAsString(effectiveDateRange[0], !this.isTranslatedUkDateLocale));
                    params.append('StopDate_DATA2', formatDateAsString(effectiveDateRange[1], !this.isTranslatedUkDateLocale));
                }

                [STATUS_FILTER.DONE, STATUS_FILTER.QUEUED].forEach((status) => params.append('Status_DATA', status));
            } else {
                params.append('reportClass', 'FunnelGoalAchieved');
                params.append('Goal_DATA', `${funnelInfo.funnelId}:${contextMenuData.cell.id}`);

                if (effectiveDateRange) {
                    params.append('GoalCompletionDate_DATA', formatDateAsString(effectiveDateRange[0], !this.isTranslatedUkDateLocale));
                    params.append('GoalCompletionDate_DATA2', formatDateAsString(effectiveDateRange[1], !this.isTranslatedUkDateLocale));
                }
            }

            params.append('view', 'resultsPage');
            params.append('RunSearch', 'true');
            params.append('Funnel_DATA', funnelInfo.funnelId);
            params.append('Status_DATA', (statusFilter !== STATUS_FILTER.WAITING) ? statusFilter : STATUS_FILTER.ACTIVE);
            params.append('hideSpaNav', 'true');
            params.append('fromMenu', 'true');

            window.open(`${appInfo.baseUrl}/Reports/searchTemplate.jsp?${params.toString()}`, '_blank');
            this.closeInlineAddMenu();
            this.closeInlineBetweenAddMenu();
            this.onCloseContextMenu();
        },


        onClickStage({ event }) {
            if (!event.evt.shiftKey) this.setSelectedCells([]);
            this.closeInlineAddMenu();
            this.closeInlineBetweenAddMenu();
            if (this.contextMenuData) this.onCloseContextMenu();
        },

        onCloseContextMenu() {
            if (!this.contextMenuData) return;
            this.contextMenuData = null;

            if (!this.isSequence) {
                document.querySelector('.campaign-stage-container')?.focus();
            }
        },

        onCloseSequence() {
            document.querySelector('.campaign-stage-container')?.focus();
            this.setSequenceId(MAIN_CANVAS_ID);
        },

        onUpdateFilters({ preset, dateRange }) {
            this.showReportingFilterModal = false;
            this.preset = preset;
            this.dateRange = dateRange;

            this.fetchReportData();
        },

        async fetchReportData() {
            this.reportData = {};
            const { dateRange, funnelId, preset } = this;
            let funnelStats;
            let activeStats;

            if (preset === PRESET_FILTERS.CUSTOM) {
                const startDate = formatDateAsString(dateRange[0], !this.isUkDateLocale);
                const endDate = formatDateAsString(dateRange[1], !this.isUkDateLocale);

                [this.uniqueContacts, funnelStats] = await Promise.all([
                    getDateRangeUniqueContactsStats(funnelId, startDate, endDate),
                    getDateRangeStats(funnelId, startDate, endDate),
                ]);
                this.emailStats = await getDateRangeEmailStats(funnelId, startDate, endDate);
                activeStats = null;
            } else if (preset === PRESET_FILTERS.CURRENT) {
                [this.uniqueContacts, activeStats] = await Promise.all([
                    getUniqueContactsStats(funnelId, preset),
                    getActiveStats(funnelId),
                ]);
            } else {
                [this.uniqueContacts, funnelStats] = await Promise.all([
                    getUniqueContactsStats(funnelId, preset),
                    getFunnelStats(funnelId, preset),
                ]);

                this.emailStats = await getEmailStats(funnelId, preset);
            }
            const reportData = {};

            this.cells.forEach((cell) => {
                const id = Math.abs(cell.meta.id);
                let waitingStatistics;

                if (this.isTimer(cell)) {
                    const outgoingCells = findOutgoingCells(cell.meta.id, this.cells);
                    const targetCellId = outgoingCells?.length && outgoingCells[0].id;

                    if (targetCellId) {
                        waitingStatistics = (activeStats && activeStats?.data.activeStatistics[targetCellId]) ?? 0;
                    }
                }

                reportData[id] = {
                    funnelStats: funnelStats?.data[id],
                    activeStatistics: activeStats && activeStats?.data.activeStatistics[id],
                    activeQueueStatistics: activeStats && activeStats?.data.activeQueueStatistics[id],
                    waitingQueueStatistics: waitingStatistics,
                };
            });

            this.reportData = reportData;
        },

        showHotkeys() {
            this.$emit('toggle-keyboard-shortcuts-sidebar');
        },

        saveCampaign() {
            this.$emit('save-campaign');
        },

        addCell(event) {
            this.closeInlineAddMenu();
            this.closeInlineBetweenAddMenu();
            this.$emit('add-cell', event);
        },

        toggleSequenceReady(sequenceCell) {
            this.$emit('toggle-sequence-ready', sequenceCell);
        },

        openRenameModal(cell) {
            this.$emit('open-rename-modal', cell);
        },

        openSettingsModal(cell) {
            this.$emit('open-settings-modal', cell);
        },

        onEditNote(cell) {
            const stageElement = this.isSequence
                ? document.querySelector('.campaign-stage-container[data-stage-context="sequence"] canvas')
                : document.querySelector('.campaign-stage-container[data-stage-context="main"] canvas');

            const stagePosition = stageElement.getBoundingClientRect();

            const { event } = this.contextMenuData;
            const textPosition = event.target ? event.target.getClientRect() : event.event.target.getClientRect();

            this.setEditableNameData({
                cell,
                name: cell.value.notes,
                notesColor: cell.value.notesColor || NOTE_COLOR.gray,
                position: {
                    left: stagePosition.left + textPosition.x,
                    top: stagePosition.top + textPosition.y,
                    width: cell.mxGeometry.width,
                    height: cell.mxGeometry.height,
                    transform: `scale(${this.zoomLevel})`,
                },
            });
        },

        openConfigModal(cell) {
            if (cell.type === FLOW_TYPE) {
                this.setSelectedCells([]);
                this.setSequenceId(cell.id);

                return;
            }

            if (cell.type === NOTES_TYPE) {
                this.onEditNote(cell);
                this.onCloseContextMenu();

                return;
            }

            this.closeInlineAddMenu();
            this.closeInlineBetweenAddMenu();
            this.onCloseContextMenu();
            this.$emit('open-config-modal', cell);
        },

        clickNewEmailExperience(cell) {
            this.$emit('open-new-email-experience', cell);
        },

        openFeedbackModal() {
            this.$emit('open-automation-feedback-modal');
        },

        updateCell(cell, updates, overwriteValues = false) {
            // updates = { meta: { ... }, value: { ... }, mxGeometry: { ... } }

            const { cells } = this;
            const cellIndex = cells.findIndex((draftCell) => draftCell?.meta.id === cell.meta.id);


            Object.keys(updates).forEach((key) => {
                if (key === 'type') {
                    cells[cellIndex].type = updates[key];
                    cells[cellIndex].meta.style = STYLE_BY_META_TYPE[updates[key]] || updates[key];
                } else if (overwriteValues) {
                    cells[cellIndex][key] = updates[key];
                } else {
                    Object.keys(updates[key]).forEach((propertyKey) => {
                        cells[cellIndex][key][propertyKey] = updates[key][propertyKey];
                    });
                }
            });

            this.$emit('update-cells', cells);

            return cells[cellIndex];
        },

        updateCellValue({ cell, isReady }) {
            this.$emit('update-cell-value', { cell, isReady });
        },

        updateNotesColor({ cell, color }) {
            this.updateCell(cell, { value: { notesColor: color } });
        },

        updateNote({ cell, name, mxGeometry }) {
            this.updateCell(cell, { value: { notes: name }, mxGeometry });
        },

        renameCell({ cell, name }) {
            const updatedCell = this.updateCell(cell, { value: { name } });

            this.$emit('update-cell-name', { cell: updatedCell });
        },

        updateSequenceName(eventData) {
            this.updateCell(this.currentSequenceCell, { value: { name: eventData.newSequenceName } });
        },

        openCopyFromAutomationModal() {
            this.showCopyFromAutomationModal = true;
        },

        closeCopyFromAutomationModal() {
            this.showCopyFromAutomationModal = false;
        },

        replaceSequence(replaceSequenceEvent) {
            this.$emit('replace-sequence', replaceSequenceEvent);
        },

        openEditSequenceName() {
            this.showEditSequenceName = true;
        },

        closeEditSequenceName() {
            this.showEditSequenceName = false;
        },

        setSelectedCells(selectedCells) {
            this.$emit('set-selected-cells', selectedCells);
        },

        setSequenceId(sequenceId) {
            this.closeInlineAddMenu();
            this.closeInlineBetweenAddMenu();
            this.$emit('set-sequence-id', sequenceId);
        },

        // via drag and drop
        async copyCells({ draggedCell, newPosition }) {
            const isDraggingWithGroup = this.selectedCells.map((cell) => cell.id).includes(draggedCell.id);

            if (!isDraggingWithGroup) this.setSelectedCells([draggedCell]);

            const positionOffset = {
                x: newPosition.x - draggedCell.mxGeometry.x,
                y: newPosition.y - draggedCell.mxGeometry.y,
            };

            return this.duplicateMultipleCells({ positionOffset });
        },

        async duplicateMultipleCells({ cellsToCopy = this.selectedCells, positionOffset } = {}) {
            if (containsIncompleteDecisionDiamond(cellsToCopy)) {
                this.$toast({ message: this.$t('funnel.duplicate.incomplete.decision.diamond') });

                return;
            }
            const cellMapping = {};

            const edgesToCopy = [];
            const elementsToCopy = [];

            cellsToCopy.forEach((selectedCell) => {
                if (selectedCell.type === EDGE_TYPE) edgesToCopy.push(selectedCell);
                else elementsToCopy.push(selectedCell);
            });

            this.setSelectedCells([]);

            const copiedElements = [];

            for await (const elementToCopy of elementsToCopy) {
                let newPosition;

                if (positionOffset) {
                    newPosition = {
                        x: elementToCopy.mxGeometry.x + positionOffset.x,
                        y: elementToCopy.mxGeometry.y + positionOffset.y,
                    };
                }
                const duplicatedCell = await this.duplicateSingleCell(elementToCopy, { addToSelection: true, newPosition });

                cellMapping[elementToCopy.id] = duplicatedCell;
                copiedElements.push(duplicatedCell);
            }

            this.setSelectedCells(copiedElements);

            edgesToCopy.forEach((edge) => {
                const source = cellMapping[edge.meta.source];
                const target = cellMapping[edge.meta.target];

                this.$emit('add-cell', {
                    cellName: EDGE_TYPE,
                    target: target.id,
                    source: source.id,
                    parent: edge.meta.parent,
                    autoCreateEdge: false,
                    addToSelection: true,
                });
            });
        },

        moveCells({ draggedCell, newPosition }) {
            const xOffset = newPosition.x - draggedCell.mxGeometry.x;
            const yOffset = newPosition.y - draggedCell.mxGeometry.y;

            const selectedCellIds = this.selectedCells.map((selectedCell) => selectedCell.id);

            selectedCellIds.forEach((cellId) => {
                const selectedCell = this.cells.find((cell) => cell.meta.id === cellId);

                if (selectedCell && selectedCell.type === EDGE_TYPE) return;

                const newPositionRelative = {
                    x: selectedCell.mxGeometry.x + xOffset,
                    y: selectedCell.mxGeometry.y + yOffset,
                };

                this.updateCell(selectedCell, { mxGeometry: { ...newPositionRelative }, value: { published: '0' } });
            });
        },

        deleteCell(eventCell) {
            this.deleteCells([eventCell]);
        },

        getEdgesToDelete(elementsToDelete) {
            const edgesConnected = new Set();
            const elementsToDeleteIds = new Set(elementsToDelete.map((cell) => cell.id));

            elementsToDelete.forEach((cell) => {
                if (cell.type !== EDGE_TYPE) {
                    const incomingEdges = findIncomingEdges(cell.id, this.cells);
                    const outgoingEdges = findOutgoingEdges(cell.id, this.cells);

                    (incomingEdges || []).forEach((edge) => {
                        if (!elementsToDeleteIds.has(edge.id)) {
                            edgesConnected.add(edge);
                        }
                    });

                    (outgoingEdges || []).forEach((edge) => {
                        if (!elementsToDeleteIds.has(edge.id)) {
                            edgesConnected.add(edge);
                        }
                    });
                }
            });

            return [...edgesConnected];
        },

        deleteCells(elementsToDelete = this.selectedCells) {
            const edgesToDelete = this.getEdgesToDelete([...elementsToDelete]);

            this.elementsToDelete = [...elementsToDelete, ...edgesToDelete];

            const emailConfirmCells = [];
            const outGoingDDCells = [];

            // delete anything 'linked to' things we're deleting
            this.elementsToDelete.forEach((cell) => {
                if (cell.meta.style === GOALS.GOAL_EMAIL_CONFIRM && cell.meta.metaType === GOALS.GOAL_EMAIL_LINK_CLICKED) {
                    emailConfirmCells.push(cell);
                }

                const outgoingCells = findOutgoingCells(cell.id, this.cells);
                const outgoingDecisionDiamonds = outgoingCells.filter((outgoingCell) => outgoingCell.type === DECISION_TYPE);

                outGoingDDCells.push(...outgoingDecisionDiamonds);
            });

            emailConfirmCells.forEach((cell) => {
                findLockedIncomingCells(cell.id, this.cells).forEach((linkedCell) => this.elementsToDelete.push(linkedCell));
            });

            this.elementsToDelete.push(...outGoingDDCells);

            // filter out duplicates and things that can't be deleted
            const elementIdsToDelete = [];

            this.elementsToDelete = this.elementsToDelete.filter((element) => {
                if (elementIdsToDelete.includes(element.id)) return false; // already marked for deletion

                if (element.type === EDGE_TYPE) {
                    const targetCell = this.cells.find((otherCell) => otherCell.meta.id === element.meta.target);

                    if (targetCell.type === DECISION_TYPE && !elementIdsToDelete.includes(targetCell.id)) {
                        return false; // can't delete edge to decision diamond
                    }

                    if (isEdgeFromConfirmToLinkClick(element, this.cells)) {
                        return false;
                    }
                }

                elementIdsToDelete.push(element.id);

                return true;
            });

            if (this.elementsToDelete.length && !this.showReportingView) {
                this.showDeleteDialog = true;

                if (this.elementsToDelete.length === 1 && this.elementsToDelete[0].type === EDGE_TYPE) {
                    this.confirmDeleteDialog();
                }
            }
        },

        closeDeleteDialog() {
            this.showDeleteDialog = false;
            this.elementsToDelete = [];
            this.setSelectedCells([]);
        },

        confirmDeleteDialog() {
            const selectedCellIds = [...this.elementsToDelete.map((cell) => cell.id)];

            let updatedCells = [...this.cells].filter((cell) => !selectedCellIds.includes(cell.meta.id));

            const removedFlowIds = this.cells.filter((cell) => selectedCellIds.includes(cell.meta.id) && cell.type === FLOW_TYPE)
                .map((cell) => cell.id);

            const filteredDecisionCells = updatedCells.filter((cell) => cell.meta.style === DECISION_TYPE);

            if (filteredDecisionCells.length) {
                updatedCells = processDecisionCells(filteredDecisionCells, updatedCells, removedFlowIds);
            }

            this.$emit('update-cells', updatedCells);

            if (!this.isSequence) {
                const edgesToDelete = this.elementsToDelete.filter((item) => item.type === EDGE_TYPE);
                const targetCells = edgesToDelete.map((item) => item.meta.target).filter((element) => !selectedCellIds.includes(element));

                this.$emit('update-cells-global-state', targetCells);
            }

            this.closeDeleteDialog();
        },

        async duplicateSingleCell(eventCell, { newPosition, addToSelection } = {}) {
            const newCell = await copyCell(eventCell, newPosition, this.funnelId);

            newCell.width = eventCell.mxGeometry.width;
            newCell.height = eventCell.mxGeometry.height;
            newCell.parent = eventCell?.meta.parent;
            newCell.autoCreateEdge = false;
            newCell.ignoreAutoOpen = true;

            if (newCell.addToSelection) newCell.addToSelection = addToSelection;

            this.$emit('add-cell', newCell);

            const expectedCellId = `${maxCellIdStore.maxCellId}`;

            if (eventCell.meta.style === FLOW_TYPE) {
                await this.duplicateSequence(eventCell, expectedCellId);
            }

            return { ...newCell, id: expectedCellId };
        },

        async duplicateSequence(eventCell, parentId) {
            const childrenCells = findChildren(eventCell.id, this.draftJson.cells);
            const childrenEdges = childrenCells.filter((cell) => cell.meta.style === EDGE_TYPE);
            const childrenThens = childrenCells.filter((cell) => !childrenEdges.includes(cell));

            const originalIdToCopyIdMap = {};

            for await (const child of childrenThens) {
                const childCopy = await copyCell(child, {
                    x: child.mxGeometry.x,
                    y: child.mxGeometry.y,
                }, this.funnelInfo.funnelId);

                this.$emit('add-cell', {
                    parent: parentId,
                    autoCreateEdge: false,
                    autoSelectCell: false,
                    ignoreAutoOpen: true,
                    ...childCopy,
                });

                originalIdToCopyIdMap[child.id] = maxCellIdStore.maxCellId;
            }

            for await (const edge of childrenEdges) {
                this.$emit('add-cell', {
                    cellName: EDGE_TYPE,
                    parent: parentId,
                    source: `${originalIdToCopyIdMap[edge.meta.source]}`,
                    target: `${originalIdToCopyIdMap[edge.meta.target]}`,
                    autoCreateEdge: false,
                    addToSelection: false,
                });
            }
        },

        closeConvertModal() {
            this.showConvertModal = false;
            this.bardEmailCell = undefined;
        },

        findCellIndex (cellId) {
            return this.cells.findIndex((cell) => cell.id === cellId);
        },

        isTimer(cell) {
            return Object.values(TIMERS).includes(cell?.type);
        },
    },
};
</script>

<style scoped lang="scss">
.campaign-overlay-header-buttons {
    margin: auto 0;
}
.reporting-warning {
    display: flex;
    margin: auto;
    align-items: center;
    height: 80%;
    width: fit-content;
}
.campaign-builder-wrapper {
    width: 100vw;
    height: calc(100vh - 72px);
    overflow: hidden;
}
.campaign-builder-content {
    height: 100%;
}

</style>
